import React, { useEffect, useMemo, useState } from "react";
import Modal from "../ui/Modal";
import SearchableSelect from "../ui/SearchableSelect";
import { useScheduleData } from "./ScheduleDataContext";
import {
  updateSoftSchedule, deleteSoftSchedule, postScheduledResource,
  fetchTicketsByProjectIds, getProjectIdFromTicket, getTicketSummary,
} from "../../api/client";
import { buildApiUrl, apiConfig } from "../../apiConfig";

function toDateInput(isoStr) {
  if (!isoStr) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoStr));
  if (!m) return "";
  return `${m[1]}-${m[2]}-${m[3]}`;
}
function toUtcIso(dateStr) {
  if (!dateStr) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr));
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}T00:00:00Z`;
}
function utcIsoToLocalMidnight(iso) {
  const utc = new Date(iso);
  return new Date(utc.getTime() + utc.getTimezoneOffset() * 60000);
}
function unwrapId(v) { if (v == null) return null; if (typeof v === 'object') return unwrapId(v.id ?? v.identifier ?? v.value ?? v.objectId ?? v.ticketId ?? v.projectId ?? null); return v; }
function extractProjectId(p) { return unwrapId(p?.id) ?? unwrapId(p?.projectId) ?? unwrapId(p?.project) ?? unwrapId(p?.['project/id']) ?? null; }
function extractProjectName(p) { return p?.name || p?.project?.name || `Project ${extractProjectId(p)}`; }
function extractTicketId(t) {
  const v = t?.id ?? t?.ticketId ?? t?.objectId ?? null;
  if (v && typeof v === 'object') return v.id ?? v.identifier ?? v.value ?? null;
  return v;
}

export default function SoftScheduleEditModal({ softEvent, onClose, onUpdated, onDeleted }) {
  const { token, setAssignments, setTickets, tickets, ticketProjectMap, projects, backgroundLoading } = useScheduleData();

  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");

  // Ticket / project picker state
  const [searchProject, setSearchProject] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [manualTicketId, setManualTicketId] = useState("");
  const [projectTickets, setProjectTickets] = useState([]);
  const [loadingProjectTickets, setLoadingProjectTickets] = useState(false);
  const [projectTicketsError, setProjectTicketsError] = useState(null);

  // User picker state
  const [searchUser, setSearchUser] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [saving, setSaving] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [description, setDescription] = useState("");
  const [hours, setHours] = useState("");

  // Populate fields when a soft event is opened
  useEffect(() => {
    if (!softEvent) return;
    const sd = softEvent.meta.softData;
    setDateStart(toDateInput(sd.dateStart));
    setDateEnd(toDateInput(sd.dateEnd));
    setError(null);
    setMsg(null);
    setManualTicketId("");
    setDescription(sd.description || "");
    setHours(sd.hours != null ? String(sd.hours) : "");
    setProjectTickets([]);
    setProjectTicketsError(null);
    setSearchUser("");

    // Pre-select ticket immediately
    const tid = sd.ticketId != null ? sd.ticketId : null;
    setSelectedTicketId(tid);

    // Pre-select project from map immediately
    const pid = tid != null ? (ticketProjectMap.get(tid) ?? ticketProjectMap.get(String(tid)) ?? null) : null;
    setSelectedProjectId(pid ? String(pid) : null);
    setSearchProject(pid ? String(pid) : "");

    // Pre-populate user with synthetic object so the radio is checked immediately
    // (replaced by the real user object once the users list loads)
    setSelectedUser(sd.memberIdentifier ? { identifier: sd.memberIdentifier, name: sd.memberIdentifier, _synthetic: true } : null);
  }, [softEvent, ticketProjectMap]);

  // Load users when modal opens
  useEffect(() => {
    if (!softEvent) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingUsers(true);
        const url = buildApiUrl(apiConfig.cwUsersPath, { conditions: "inactiveFlag=false" });
        const res = await fetch(url, { headers: { Accept: 'application/json', ...(token ? { Authorization: token } : {}) } });
        const data = res.ok ? await res.json() : [];
        const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        if (!cancelled) {
          setUsers(list);
          // Replace synthetic user with the real user object from the list
          const sd = softEvent.meta.softData;
          const match = list.find(u => u?.identifier === sd.memberIdentifier || String(u?.id) === sd.memberIdentifier);
          if (!cancelled) setSelectedUser(match ?? { identifier: sd.memberIdentifier, name: sd.memberIdentifier, _synthetic: true });
        }
      } catch {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [softEvent, token]);

  // Fetch tickets when project selected
  useEffect(() => {
    if (!softEvent || !selectedProjectId) { setProjectTickets([]); setProjectTicketsError(null); return; }
    let cancelled = false;
    (async () => {
      try {
        setProjectTicketsError(null);
        setLoadingProjectTickets(true);
        const list = await fetchTicketsByProjectIds([selectedProjectId], token, { pageSize: 200 });
        if (!cancelled) setProjectTickets(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) { setProjectTickets([]); setProjectTicketsError(e); }
      } finally {
        if (!cancelled) setLoadingProjectTickets(false);
      }
    })();
    return () => { cancelled = true; };
  }, [softEvent, selectedProjectId, token]);

  const filteredProjects = useMemo(() => {
    const n = searchProject.trim().toLowerCase();
    const arr = projects || [];
    if (!n) return arr;
    return arr.filter(p => {
      const id = String(extractProjectId(p) ?? "");
      const nm = (extractProjectName(p) || '').toLowerCase();
      return id.includes(n) || nm.includes(n);
    });
  }, [projects, searchProject]);

  function applyProjectSearch() {
    const raw = searchProject.trim();
    if (!raw) return;
    const exact = filteredProjects.find(p => String(extractProjectId(p)) === raw);
    let idToUse = null;
    if (exact) idToUse = extractProjectId(exact);
    else if (filteredProjects.length > 0) idToUse = extractProjectId(filteredProjects[0]);
    else if (/^\d+$/.test(raw)) idToUse = raw;
    if (!idToUse) return;
    setSelectedProjectId(String(idToUse));
    setSelectedTicketId(null);
    setManualTicketId("");
  }

  const ticketsForProject = useMemo(() => {
    if (!selectedProjectId) return [];
    const all = tickets || [];
    const scoped = all.filter(t => {
      const tid = extractTicketId(t);
      const pidFromMap = tid != null ? ticketProjectMap.get(tid) : null;
      if (pidFromMap != null) return String(pidFromMap) === String(selectedProjectId);
      const pid = getProjectIdFromTicket(t);
      return pid != null && String(pid) === String(selectedProjectId);
    });
    const remote = projectTickets || [];
    const seen = new Set();
    const merged = [];
    const pushIfNew = (t) => {
      const id = extractTicketId(t);
      const key = id != null ? String(id) : JSON.stringify(t);
      if (!seen.has(key)) { seen.add(key); merged.push(t); }
    };
    scoped.forEach(pushIfNew);
    remote.forEach(pushIfNew);
    return merged;
  }, [tickets, ticketProjectMap, selectedProjectId, projectTickets]);

  // Ticket items for SearchableSelect — include a fallback entry for the pre-selected ticket
  // so the dropdown shows it selected immediately even before the async API fetch completes.
  const ticketSelectItems = useMemo(() => {
    const base = ticketsForProject.slice(0, 1000).map(t => ({
      value: extractTicketId(t),
      label: `${extractTicketId(t)} • ${getTicketSummary(t) || ''}`,
    }));
    if (selectedTicketId != null && !base.some(i => String(i.value) === String(selectedTicketId))) {
      base.unshift({ value: selectedTicketId, label: `Ticket ${selectedTicketId}` });
    }
    return base;
  }, [ticketsForProject, selectedTicketId]);

  const filteredUsers = useMemo(() => {
    const n = searchUser.trim().toLowerCase();
    const arr = users || [];
    if (!n) return arr;
    return arr.filter(u => {
      const id = String(u?.id ?? "");
      const nm = (u?.name || u?.fullName || u?.identifier || '').toLowerCase();
      return id.includes(n) || nm.includes(n);
    });
  }, [users, searchUser]);

  if (!softEvent) return null;
  const { softData } = softEvent.meta;

  const effectiveTicketId = selectedTicketId ?? softData.ticketId;
  const effectiveMember = selectedUser?.identifier || String(selectedUser?.id ?? softData.memberIdentifier);

  async function onSave() {
    try {
      setError(null); setMsg(null);
      const ds = toUtcIso(dateStart);
      const de = toUtcIso(dateEnd);
      if (!ds || !de) throw new Error("Invalid dates");
      if (new Date(de) < new Date(ds)) throw new Error("End must be after start");
      setSaving(true);
      const patch = {
        dateStart: ds,
        dateEnd: de,
        ticketId: effectiveTicketId,
        memberIdentifier: effectiveMember,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(hours !== '' && Number(hours) > 0 ? { hours: Number(hours) } : { hours: null }),
      };
      const updated = await updateSoftSchedule(softData.id, patch, token);
      onUpdated?.({ ...softData, ...patch, ...(updated || {}) });
      setMsg("Saved");
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  }

  async function onScheduleResource() {
    try {
      setError(null); setMsg(null);
      const ds = toUtcIso(dateStart);
      const de = toUtcIso(dateEnd);
      if (!ds || !de) throw new Error("Invalid dates");
      if (new Date(de) < new Date(ds)) throw new Error("End must be after start");
      setScheduling(true);

      const ticketId = typeof effectiveTicketId === "number"
        ? effectiveTicketId
        : parseInt(String(effectiveTicketId), 10);

      const payload = {
        objectId: ticketId,
        member: { identifier: effectiveMember },
        dateStart: ds, dateEnd: de,
        type: { identifier: "S" },
        span: { identifier: "N" },
      };

      const created = await postScheduledResource(payload, token);

      // Resolve resourceIdentifier from the API response first, then fall back to selected user
      const resourceIdentifier =
        created?.member?.id ??
        created?.memberId ??
        created?.member?.identifier ??
        selectedUser?.id ??
        selectedUser?.identifier ??
        null;

      const projectId = ticketProjectMap.get(ticketId) || selectedProjectId || null;

      // Find ticket object — check context first, then projectTickets loaded in this modal
      let ticketObj = (tickets || []).find(t => {
        const tid = t?.id ?? t?.ticketId ?? t?.objectId;
        return tid != null && String(tid) === String(ticketId);
      });
      if (!ticketObj) {
        ticketObj = (projectTickets || []).find(t => String(extractTicketId(t)) === String(ticketId));
      }
      const ticketSummary = ticketObj ? ticketObj.summary || ticketObj.name || ticketObj.subject || null : null;

      // Ensure the ticket is in the context's tickets array so filteredAssignments doesn't filter it out
      const ticketInContext = (tickets || []).some(t => {
        const tid = t?.id ?? t?.ticketId ?? t?.objectId;
        return tid != null && String(tid) === String(ticketId);
      });
      if (!ticketInContext) {
        const syntheticTicket = ticketObj ?? { id: ticketId, ticketId };
        setTickets(prev => [...(prev || []), syntheticTicket]);
      }

      setAssignments(prev => [...prev, {
        id: created?.id ?? Math.random(),
        ticketId, projectId,
        start: utcIsoToLocalMidnight(ds),
        end: utcIsoToLocalMidnight(de),
        resourceName: effectiveMember,
        resourceIdentifier,
        ticketSummary,
        raw: created || payload,
      }]);

      await deleteSoftSchedule(softData.id, token);
      onDeleted?.(softData.id);
      onClose?.();
    } catch (e) {
      setError(e);
    } finally {
      setScheduling(false);
    }
  }

  const busy = saving || scheduling;

  return (
    <Modal onClose={onClose}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>Edit Soft Schedule</h3>
        <button onClick={onClose} disabled={busy}>Close</button>
      </div>
      <div style={{ marginTop: 12, display: "grid", gap: 12, color: 'var(--white_coordinating, #ffffff)' }}>
        {error && <div style={{ color: "#ffb3b3" }}>Error: {error.message}</div>}
        {msg && <div style={{ color: "#a5d6a7" }}>{msg}</div>}

        {/* Description (shown when no ticket, or always editable) */}
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Description <span style={{ fontWeight: 400, color: '#aaa', fontSize: 12 }}>(optional — shown on bar when no project info)</span></div>
          <input
            type="text"
            placeholder="e.g. On-call coverage, Buffer time…"
            value={description}
            onChange={e => setDescription(e.target.value)}
            style={{ width: '100%', padding: '6px 8px', background: '#fff', color: '#000', border: '1px solid #ccc', borderRadius: 4 }}
          />
        </div>

        {/* Project */}
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Project</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              placeholder="Search project id or name"
              value={searchProject}
              onChange={e => setSearchProject(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyProjectSearch(); } }}
              style={{ flex: 1, padding: '6px 8px', background: '#fff', color: '#000', border: '1px solid #ccc', borderRadius: 4 }}
            />
            <button onClick={applyProjectSearch} style={{ background: '#fff', color: '#000', border: '1px solid #ccc', borderRadius: 4, padding: '6px 10px' }}>Search</button>
          </div>
          <div style={{ maxHeight: 140, overflow: 'auto', border: '1px solid #ccc', background: '#fff', color: '#000', marginTop: 4 }}>
            {filteredProjects.slice(0, 200).map(p => {
              const id = extractProjectId(p);
              const checked = String(selectedProjectId) === String(id);
              return (
                <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px' }}>
                  <input type="radio" name="edit_soft_proj" checked={checked} onChange={() => { setSelectedProjectId(String(id)); setSelectedTicketId(null); }} />
                  <span style={{ color: '#000' }}>{id} • {extractProjectName(p)}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Ticket */}
        <div style={{ opacity: selectedProjectId ? 1 : 0.4, pointerEvents: selectedProjectId ? 'auto' : 'none', transition: 'opacity 160ms' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Ticket</div>
          <SearchableSelect
            disabled={!selectedProjectId}
            items={ticketSelectItems}
            value={selectedTicketId}
            onChange={val => setSelectedTicketId(val)}
            placeholder="Search tickets by id or summary"
          />
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="text"
              placeholder="Or enter Ticket ID manually"
              value={manualTicketId}
              onChange={e => setManualTicketId(e.target.value)}
              disabled={!selectedProjectId}
              style={{ flex: 1, padding: '6px 8px', background: '#fff', color: '#000', border: '1px solid #ccc', borderRadius: 4 }}
            />
            <button
              type="button"
              onClick={() => {
                const v = String(manualTicketId || '').trim();
                if (!v) return;
                setSelectedTicketId(/^\d+$/.test(v) ? Number(v) : v);
              }}
              disabled={!selectedProjectId || !String(manualTicketId || '').trim()}
              style={{ background: '#fff', color: '#000', border: '1px solid #ccc', borderRadius: 4, padding: '6px 10px' }}
            >
              Use ID
            </button>
          </div>
          {selectedProjectId && (
            <div style={{ marginTop: 4, fontSize: 12, color: '#ddd' }}>
              {loadingProjectTickets ? `Fetching tickets for project ${selectedProjectId}…` : projectTicketsError ? <span style={{ color: '#ffb3b3' }}>Failed to fetch project tickets</span> : `${ticketsForProject.length} tickets for project ${selectedProjectId}`}
              {backgroundLoading && ' • still loading in background'}
            </div>
          )}
        </div>

        {/* User */}
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Member</div>
          <input
            placeholder="Search user id or name"
            value={searchUser}
            onChange={e => setSearchUser(e.target.value)}
            style={{ width: '100%', padding: '6px 8px', marginBottom: 6, background: '#fff', color: '#000', border: '1px solid #ccc', borderRadius: 4 }}
          />
          <div style={{ maxHeight: 140, overflow: 'auto', border: '1px solid #ccc', background: '#fff', color: '#000' }}>
            {loadingUsers && selectedUser?._synthetic && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', background: '#f5f5f5' }}>
                <input type="radio" name="edit_soft_user" checked readOnly />
                <span style={{ color: '#000' }}>{selectedUser.identifier} (loading…)</span>
              </label>
            )}
            {!loadingUsers && filteredUsers.slice(0, 300).map(u => {
              const id = u?.id;
              const name = u?.name || u?.fullName || u?.identifier || `User ${id}`;
              const checked = selectedUser
                ? (selectedUser._synthetic
                  ? selectedUser.identifier === u?.identifier
                  : selectedUser.id === id)
                : false;
              return (
                <label key={id ?? name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px' }}>
                  <input type="radio" name="edit_soft_user" checked={checked} onChange={() => setSelectedUser(u)} />
                  <span style={{ color: '#000' }}>{id ?? ''}{id ? ' • ' : ''}{name}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Dates */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Start Date</div>
            <input type="date" value={dateStart} onChange={e => setDateStart(e.target.value)}
              style={{ padding: "4px 6px", background: '#fff', color: '#000', border: "1px solid #ccc", borderRadius: 4 }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>End Date</div>
            <input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)}
              style={{ padding: "4px 6px", background: '#fff', color: '#000', border: "1px solid #ccc", borderRadius: 4 }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Hours <span style={{ fontWeight: 400, color: '#aaa', fontSize: 12 }}>(optional)</span></div>
            <input
              type="number" min="0" step="0.5" placeholder="e.g. 8"
              value={hours}
              onChange={e => setHours(e.target.value)}
              style={{ width: 90, padding: '4px 6px', background: '#fff', color: '#000', border: '1px solid #ccc', borderRadius: 4 }}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
          <button onClick={onSave} disabled={busy}
            style={{ padding: "6px 14px", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
          <button onClick={onScheduleResource} disabled={busy || !effectiveTicketId}
            title={!effectiveTicketId ? "Select a project and ticket above before scheduling" : undefined}
            style={{ padding: "6px 14px", background: effectiveTicketId ? "#1976d2" : "#888", color: "#fff", border: "none", borderRadius: 4, cursor: effectiveTicketId ? "pointer" : "not-allowed", fontWeight: 600 }}>
            {scheduling ? "Scheduling…" : "Schedule Resource"}
          </button>
          <button onClick={async () => {
            if (!window.confirm("Delete this soft schedule?")) return;
            try {
              await deleteSoftSchedule(softData.id, token);
              onDeleted?.(softData.id);
              onClose?.();
            } catch (e) { setError(e); }
          }} disabled={busy}
            style={{ padding: "6px 14px", background: "#c62828", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>
            Delete
          </button>
        </div>
        {!effectiveTicketId && (
          <div style={{ fontSize: 12, color: '#ffb3b3', marginTop: 2 }}>
            A project and ticket must be selected above to use "Schedule Resource".
          </div>
        )}
        <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>
          "Schedule Resource" creates a real schedule entry and removes this soft schedule.
        </div>
      </div>
    </Modal>
  );
}
